import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Image, Tag } from '@frontend/core';
import { EMPTY, Observable, of, throwError, timer } from 'rxjs';
import { catchError, expand, filter, map, switchMap, take } from 'rxjs/operators';
import { ErrorService } from '../error';
import {
  ChannelLink,
  Chapter,
  Clip,
  CrewMember,
  Cut,
  Episode,
  EpisodeLink,
  Quotation,
  Season,
  Show,
  ShowsItemRef,
  ShowsPage,
  ShowsQuery,
  ShowsTranslationState,
  ShowsSubject,
  Tool,
  Translated,
} from './shows.model';

const JSON_API_MEDIA_TYPE = 'application/vnd.api+json';

/** What each page includes: everything it shows. */
export const SHOW_INCLUDES = ['seasons', 'crew', 'images', 'cultures', 'topics', 'tags'];
export const EPISODE_INCLUDES = ['cuts', 'chapters', 'crew', 'tools', 'clips', 'links', 'quotations', 'images', 'cultures', 'topics', 'tags'];
export const CLIP_INCLUDES = ['crew', 'tools', 'images', 'cultures', 'topics', 'tags'];
export const TOOL_INCLUDES = ['episodes', 'clips', 'images'];

type ResourceIdentifier = { type: string; id: string };

export type ResourceObject = ResourceIdentifier & {
  attributes: Record<string, any>;
  relationships?: Record<string, { data: ResourceIdentifier[] | ResourceIdentifier | null }>;
  meta?: Record<string, any>;
};

type SingleDocument = { data: ResourceObject; included?: ResourceObject[] };

type CollectionDocument = {
  data: ResourceObject[];
  included?: ResourceObject[];
  links: ShowsPage<unknown>['links'];
  meta: ShowsPage<unknown>['meta'];
};

/**
 * Reads the Shows section from the backend's frontend shows API — `docs/api/frontend-shows-api.md`
 * in the backend repository — and normalises its JSON:API documents into the records of
 * `shows.model.ts`.
 *
 * The language comes from `Accept-Language`, set on every request by the language interceptor;
 * each record's `requestedLocale` and `servedLocale` say what was asked for and what arrived.
 * Translation and its polling work as on the news pages.
 */
@Injectable({
  providedIn: 'root',
})
export class ShowsService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);

  private static readonly POLL_INTERVAL_SECONDS = 5;
  private static readonly POLL_MAXIMUM_INTERVAL_SECONDS = 30;
  private static readonly POLL_MAXIMUM_ATTEMPTS = 12;

  private readonly headers = new HttpHeaders({ Accept: JSON_API_MEDIA_TYPE });

  getShows(query: ShowsQuery = {}): Observable<ShowsPage<Show>> {
    return this.getPage('api/v1/shows', query, (resource, included) => this.toShow(resource, included));
  }

  getShow(slug: string): Observable<Show> {
    return this.getOne(ShowsService.path({ kind: 'show', slug }), SHOW_INCLUDES, (resource, included) => this.toShow(resource, included));
  }

  /** One show's episodes, or, with no show, every show's. */
  getEpisodes(show: string | null, query: ShowsQuery = {}): Observable<ShowsPage<Episode>> {
    const path = show === null ? 'api/v1/episodes' : ShowsService.path({ kind: 'show', slug: show }) + '/episodes';

    return this.getPage(path, query, (resource, included) => this.toEpisode(resource, included));
  }

  getEpisode(show: string, slug: string): Observable<Episode> {
    return this.getOne(ShowsService.path({ kind: 'episode', show, slug }), EPISODE_INCLUDES, (resource, included) => this.toEpisode(resource, included));
  }

  /** An episode's title alone, for naming it in a breadcrumb. */
  getEpisodeTitle(show: string, slug: string): Observable<string> {
    const params = new HttpParams().set('fields[episodes]', 'title');

    return this.http.get<SingleDocument>(ShowsService.path({ kind: 'episode', show, slug }), { headers: this.headers, params }).pipe(
      map((document) => String(document.data.attributes['title'] ?? '')),
      catchError((error) => this.handleError(error)),
    );
  }

  getClips(show: string, query: ShowsQuery = {}): Observable<ShowsPage<Clip>> {
    return this.getPage(ShowsService.path({ kind: 'show', slug: show }) + '/clips', query, (resource, included) => this.toClip(resource, included));
  }

  getClip(show: string, slug: string): Observable<Clip> {
    return this.getOne(ShowsService.path({ kind: 'clip', show, slug }), CLIP_INCLUDES, (resource, included) => this.toClip(resource, included));
  }

  getTools(query: ShowsQuery = {}): Observable<ShowsPage<Tool>> {
    return this.getPage('api/v1/tools', query, (resource, included) => this.toTool(resource, included));
  }

  getTool(slug: string): Observable<Tool> {
    return this.getOne(ShowsService.path({ kind: 'tool', slug }), TOOL_INCLUDES, (resource, included) => this.toTool(resource, included));
  }

  /**
   * Ask for an item in `locale`. Asking for an episode asks for its chapters too, and for a show
   * its seasons. The answer is the item as far as it exists in that locale, without the records it
   * relates to: keep the ones the page already has.
   */
  requestTranslation<T extends Translated>(ref: ShowsItemRef, locale: string): Observable<T> {
    return this.http
      .post<SingleDocument>(ShowsService.path(ref) + '/translations/' + encodeURIComponent(locale), null, { headers: this.headers })
      .pipe(
        map((document) => this.normaliseItem(ref, document.data, document.included ?? []) as T),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Fetch the item until its translation stops being worth waiting for, then emit it once.
   * Emits nothing if it never settles within the attempts allowed, or if a poll fails: the page
   * already shows the English.
   */
  watchTranslation<T extends Translated>(ref: ShowsItemRef): Observable<T> {
    let attempts = 0;
    const poll = () => (this.getItem(ref) as Observable<T>).pipe(catchError(() => of(null)));

    return timer(this.intervalFor(1)).pipe(
      switchMap(poll),
      expand((item) => {
        attempts++;

        if (item === null || !item.worthPolling || attempts >= ShowsService.POLL_MAXIMUM_ATTEMPTS) {
          return EMPTY;
        }

        return timer(this.intervalFor(attempts + 1)).pipe(switchMap(poll));
      }),
      filter((item): item is T => item !== null && !item.worthPolling),
      take(1),
    );
  }

  /** Whether the signed-in reader likes the item. Never a count: likes are private. */
  getLike(ref: ShowsItemRef): Observable<boolean> {
    return this.http.get<{ meta: { liked: boolean } }>(ShowsService.path(ref) + '/like', { headers: this.headers }).pipe(
      map((answer) => !!answer.meta?.liked),
      catchError((error) => this.handleError(error)),
    );
  }

  setLike(ref: ShowsItemRef, liked: boolean): Observable<boolean> {
    const url = ShowsService.path(ref) + '/like';
    const request = liked ? this.http.put<{ meta: { liked: boolean } }>(url, null, { headers: this.headers }) : this.http.delete<{ meta: { liked: boolean } }>(url, { headers: this.headers });

    return request.pipe(
      map((answer) => !!answer.meta?.liked),
      catchError((error) => this.handleError(error)),
    );
  }

  /** Whether a page showing this record should ask for its translation. */
  static needsTranslation(record: Translated): boolean {
    return record.servedLocale !== record.requestedLocale && (record.translationState === 'absent' || record.translationState === 'failed');
  }

  static path(ref: ShowsItemRef): string {
    const e = encodeURIComponent;

    switch (ref.kind) {
      case 'show':
        return `api/v1/shows/${e(ref.slug)}`;
      case 'episode':
        return `api/v1/shows/${e(ref.show)}/episodes/${e(ref.slug)}`;
      case 'clip':
        return `api/v1/shows/${e(ref.show)}/clips/${e(ref.slug)}`;
      case 'tool':
        return `api/v1/tools/${e(ref.slug)}`;
    }
  }

  static toParams(query: ShowsQuery): HttpParams {
    let params = new HttpParams();
    const f = query.filter ?? {};

    for (const key of ['show', 'season', 'culture', 'topic', 'tag', 'kind', 'type'] as const) {
      const values = f[key] as (string | number)[] | undefined;

      if (values?.length) {
        params = params.set(`filter[${key}]`, values.join(','));
      }
    }

    if (f.featured !== undefined) {
      params = params.set('filter[featured]', f.featured ? 'true' : 'false');
    }

    if (f.search && f.search.trim().length >= 3) {
      params = params.set('filter[search]', f.search.trim());
    }

    if (query.sort) {
      params = params.set('sort', query.sort);
    }

    if (query.page) {
      params = params.set('page[number]', String(query.page));
    }

    if (query.pageSize) {
      params = params.set('page[size]', String(query.pageSize));
    }

    if (query.include?.length) {
      params = params.set('include', query.include.join(','));
    }

    return params;
  }

  private getItem(ref: ShowsItemRef): Observable<Translated> {
    switch (ref.kind) {
      case 'show':
        return this.getShow(ref.slug);
      case 'episode':
        return this.getEpisode(ref.show, ref.slug);
      case 'clip':
        return this.getClip(ref.show, ref.slug);
      case 'tool':
        return this.getTool(ref.slug);
    }
  }

  private normaliseItem(ref: ShowsItemRef, resource: ResourceObject, included: ResourceObject[]): Translated {
    switch (ref.kind) {
      case 'show':
        return this.toShow(resource, included);
      case 'episode':
        return this.toEpisode(resource, included);
      case 'clip':
        return this.toClip(resource, included);
      case 'tool':
        return this.toTool(resource, included);
    }
  }

  private getPage<T>(path: string, query: ShowsQuery, normalise: (resource: ResourceObject, included: ResourceObject[]) => T): Observable<ShowsPage<T>> {
    return this.http.get<CollectionDocument>(path, { headers: this.headers, params: ShowsService.toParams(query) }).pipe(
      map((document) => ({
        items: document.data.map((resource) => normalise(resource, document.included ?? [])),
        links: document.links,
        meta: document.meta,
      })),
      catchError((error) => this.handleError(error)),
    );
  }

  private getOne<T>(path: string, include: string[], normalise: (resource: ResourceObject, included: ResourceObject[]) => T): Observable<T> {
    return this.http.get<SingleDocument>(path, { headers: this.headers, params: new HttpParams().set('include', include.join(',')) }).pipe(
      map((document) => normalise(document.data, document.included ?? [])),
      catchError((error) => this.handleError(error)),
    );
  }

  private related(resource: ResourceObject, included: ResourceObject[], relation: string): ResourceObject[] {
    const linkage = resource.relationships?.[relation]?.data;
    const identifiers = Array.isArray(linkage) ? linkage : linkage ? [linkage] : [];

    return identifiers
      .map((identifier) => included.find((r) => r.type === identifier.type && r.id === identifier.id))
      .filter((r): r is ResourceObject => !!r);
  }

  private translated(resource: ResourceObject): Translated {
    return {
      requestedLocale: resource.meta?.['requested_locale'] ?? '',
      servedLocale: resource.meta?.['served_locale'] ?? '',
      translationState: (resource.meta?.['translation_state'] as ShowsTranslationState) ?? null,
      worthPolling: !!resource.meta?.['worth_polling'],
    };
  }

  toShow(resource: ResourceObject, included: ResourceObject[]): Show {
    const a = resource.attributes;
    const rel = (name: string) => this.related(resource, included, name);

    return {
      ...this.translated(resource),
      id: resource.id,
      slug: a['slug'],
      name: a['name'] ?? '',
      tagline: a['tagline'] ?? null,
      description: a['description'] ?? null,
      language: a['language'] ?? null,
      colours: a['colours']?.primary && a['colours']?.accent ? { primary: a['colours'].primary, accent: a['colours'].accent } : null,
      publishedAt: a['published_at'] ? new Date(a['published_at']) : null,
      channelLinks: this.toChannelLinks(a['channel_links']),
      seasons: rel('seasons').map((r) => this.toSeason(r)),
      crew: rel('crew').map((r) => this.toCrewMember(r)),
      images: rel('images').map((r) => this.toImage(r)),
      cultures: rel('cultures').map((r) => this.toSubject(r)),
      topics: rel('topics').map((r) => this.toSubject(r)),
      tags: rel('tags').map((r) => this.toTag(r)),
    };
  }

  toEpisode(resource: ResourceObject, included: ResourceObject[]): Episode {
    const a = resource.attributes;
    const rel = (name: string) => this.related(resource, included, name);

    return {
      ...this.translated(resource),
      id: resource.id,
      slug: a['slug'],
      show: a['show'] ?? '',
      season: a['season'] ?? null,
      number: a['number'] ?? null,
      title: a['title'] ?? '',
      summary: a['summary'] ?? null,
      notes: a['notes'] ?? null,
      language: a['language'] ?? null,
      featured: !!a['featured'],
      publishedAt: a['published_at'] ? new Date(a['published_at']) : null,
      related: (a['related'] ?? []).map((r: any) => ({ type: r.type, id: String(r.id), slug: r.slug ?? null, title: r.title ?? null })),
      cuts: rel('cuts').map((r) => this.toCut(r)),
      chapters: rel('chapters')
        .map((r) => this.toChapter(r))
        .sort((x, y) => x.position - y.position),
      crew: rel('crew').map((r) => this.toCrewMember(r)),
      tools: rel('tools').map((r) => this.toTool(r, included)),
      clips: rel('clips').map((r) => this.toClip(r, included)),
      links: rel('links').map((r) => this.toLink(r)),
      quotations: rel('quotations').map((r) => this.toQuotation(r)),
      images: rel('images').map((r) => this.toImage(r)),
      cultures: rel('cultures').map((r) => this.toSubject(r)),
      topics: rel('topics').map((r) => this.toSubject(r)),
      tags: rel('tags').map((r) => this.toTag(r)),
    };
  }

  toClip(resource: ResourceObject, included: ResourceObject[]): Clip {
    const a = resource.attributes;
    const rel = (name: string) => this.related(resource, included, name);

    return {
      ...this.translated(resource),
      id: resource.id,
      slug: a['slug'],
      show: a['show'] ?? '',
      kind: a['kind'],
      title: a['title'] ?? '',
      summary: a['summary'] ?? null,
      language: a['language'] ?? null,
      durationSeconds: a['duration_seconds'] ?? null,
      publishedAt: a['published_at'] ? new Date(a['published_at']) : null,
      owner: a['owner'] ?? null,
      source: a['source'] ? { cut: String(a['source'].cut), startsAtSeconds: a['source'].starts_at_seconds ?? null } : null,
      channelLinks: this.toChannelLinks(a['channel_links']),
      crew: rel('crew').map((r) => this.toCrewMember(r)),
      tools: rel('tools').map((r) => this.toTool(r, included)),
      images: rel('images').map((r) => this.toImage(r)),
    };
  }

  toTool(resource: ResourceObject, included: ResourceObject[]): Tool {
    const a = resource.attributes;
    const rel = (name: string) => this.related(resource, included, name);

    return {
      ...this.translated(resource),
      id: resource.id,
      slug: a['slug'],
      type: a['type'],
      name: a['name'] ?? '',
      summary: a['summary'] ?? null,
      description: a['description'] ?? null,
      url: a['url'] ?? null,
      recommendedBy: a['recommended_by'] ?? [],
      episodes: rel('episodes').map((r) => this.toEpisode(r, included)),
      // A clip names its tools, which would name their clips again: read a tool's clips without
      // their relations — the tool page needs only what plays and where it comes from.
      clips: rel('clips').map((r) => this.toClip(r, [])),
      images: rel('images').map((r) => this.toImage(r)),
    };
  }

  private toSeason(resource: ResourceObject): Season {
    const a = resource.attributes;

    return { ...this.translated(resource), id: resource.id, number: a['number'], title: a['title'] ?? null, description: a['description'] ?? null };
  }

  private toCut(resource: ResourceObject): Cut {
    const a = resource.attributes;

    return {
      id: resource.id,
      medium: a['medium'],
      language: a['language'] ?? null,
      durationSeconds: a['duration_seconds'] ?? null,
      channelLinks: this.toChannelLinks(a['channel_links']),
    };
  }

  private toChapter(resource: ResourceObject): Chapter {
    const a = resource.attributes;

    return {
      ...this.translated(resource),
      id: resource.id,
      position: a['position'],
      title: a['title'] ?? '',
      description: a['description'] ?? null,
      times: (a['times'] ?? []).map((t: any) => ({ cut: String(t.cut), startsAtSeconds: t.starts_at_seconds })),
    };
  }

  private toCrewMember(resource: ResourceObject): CrewMember {
    const a = resource.attributes;

    return { id: resource.id, role: a['role'], name: a['name'] ?? '', url: a['url'] ?? null, isMember: !!a['is_member'] };
  }

  private toLink(resource: ResourceObject): EpisodeLink {
    const a = resource.attributes;

    return { id: resource.id, url: a['url'], label: a['label'] ?? null, ctaLabel: a['cta_label'] ?? null, description: a['description'] ?? null };
  }

  private toQuotation(resource: ResourceObject): Quotation {
    const a = resource.attributes;

    return {
      id: resource.id,
      text: a['text'] ?? null,
      originator: a['originator'] ?? null,
      originatorDescription: a['originator_description'] ?? null,
      source: a['source'] ?? null,
    };
  }

  private toChannelLinks(links: any[] | undefined): ChannelLink[] {
    return (links ?? []).map((link) => ({ channel: link.channel, url: link.url, externalId: link.external_id ?? null, isPlayer: !!link.is_player }));
  }

  private toSubject(resource: ResourceObject): ShowsSubject {
    return { id: +resource.id, name: resource.attributes['name'], slug: resource.attributes['slug'] };
  }

  private toTag(resource: ResourceObject): Tag {
    return new Tag(+resource.id, resource.attributes['name'], resource.attributes['slug'], resource.attributes['tag_type'], null, null, null);
  }

  private toImage(resource: ResourceObject): Image {
    const a = resource.attributes;

    return {
      id: +resource.id,
      disk: a['disk'],
      path: a['path'],
      width: a['width'] ?? null,
      height: a['height'] ?? null,
      alt_text: a['alt_text'] ?? null,
      title: a['title'] ?? null,
      caption: a['caption'] ?? null,
      credits: a['credits'] ?? null,
      licence: a['licence'] ?? null,
      role: a['role'] ?? undefined,
    };
  }

  private intervalFor(attempt: number): number {
    return Math.min(ShowsService.POLL_INTERVAL_SECONDS * attempt, ShowsService.POLL_MAXIMUM_INTERVAL_SECONDS) * 1000;
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => this.errorService.normaliseHttpError(error, 'error.something_went_wrong'));
  }
}
